import { OptionType, Security, SecurityCategory_CategoryCode, Segment } from "../proto/generated/security";
import { getAllSecurities } from "../services/apis/securities";
import { getSecurityKey } from "./security";

class SecurityManager {

    securities: Map<string, Security> = new Map();
    securityList: Security[] = [];
    async loadSecurities(): Promise<void> {
        const securitiesResponse = await getAllSecurities()
        this.securities = new Map()
        this.securityList = []

        securitiesResponse.exchangeSecurities.forEach((exchangeSecurityList) => {
            const exchange = exchangeSecurityList.exchange
            exchangeSecurityList.cashSecurities.forEach((cashSecurity) => {
                const { foData, ...cs } = cashSecurity
                const sec: Security = {
                    ...cs,
                    exchange,
                    derived: false,
                    underlyingSecurityCode: cs.code,
                    segment: Segment.CM,
                    exchangeSymbol: cs.exchangeSymbol
                };
                this.securities.set(getSecurityKey(exchange, cs.code), sec);
                this.securityList.push(sec);
                const cashCategory = cs.categoryCode
                const futureInstrument = cashCategory === SecurityCategory_CategoryCode.STOCKS ? 'FUTSTK' : cashCategory === SecurityCategory_CategoryCode.CURRENCY ? 'FUTCUR' : 'FUTIDX'
                const optionInstrument = cashCategory === SecurityCategory_CategoryCode.STOCKS ? 'OPTSTK' : cashCategory === SecurityCategory_CategoryCode.CURRENCY ? 'OPTCUR' : 'OPTIDX'
                foData?.derivedSecurities.forEach((derivedSecurity) => {
                    const sec: Security = {
                        ...cs,
                        ...derivedSecurity,
                        exchange,
                        hasFo: false,
                        segment: Segment.FO,
                        derived: true,
                        underlyingSecurityCode: cs.code,
                        series: 'XX'
                    };
                    this.securities.set(getSecurityKey(exchange, derivedSecurity.code), sec)
                    this.securityList.push(sec);
                })
                foData?.futuresSecurities.forEach((futureSecurity) => {
                    const sec: Security = {
                        ...cs,
                        ...futureSecurity,
                        segment: Segment.FO,
                        series: 'XX',
                        exchange,
                        hasFo: false,
                        derived: false,
                        categoryCode: SecurityCategory_CategoryCode.FUTURES,
                        underlyingSecurityCode: cs.code,
                        instrument: futureInstrument,
                        exchangeSymbol: cs.exchangeSymbol,
                        name: futureSecurity.name
                    };
                    this.securities.set(getSecurityKey(exchange, futureSecurity.code), sec)
                    this.securityList.push(sec);
                })
                foData?.optionsSecurities.forEach((optionSecurity) => {
                    const displayNamePrefix = optionSecurity.displayNamePrefix ?? ''
                    const symbolPrefix = optionSecurity.symbolPrefix ?? ''
                    const codePrefix = optionSecurity.securityCodePrefix ?? ''
                    optionSecurity.strikes.forEach((strike) => {
                        const ceCode = `${codePrefix}${strike}_CE`;
                        let sec: Security = {
                            ...cs,
                            priceMultiplier: optionSecurity.priceMultiplier,
                            lotSize: optionSecurity.lotSize,
                            volumeFreezeQuantity: optionSecurity.volumeFreezeQuantity,
                            tickSize: optionSecurity.tickSize,
                            expiry: optionSecurity.expiry,
                            segment: Segment.FO,
                            series: 'XX',
                            symbol: `${symbolPrefix} ${(strike / (optionSecurity.priceMultiplier ?? 100)).toFixed(0)} CE`,
                            displayName: `${displayNamePrefix} ${(strike / (optionSecurity.priceMultiplier ?? 100)).toFixed(0)} CE`,
                            code: ceCode,
                            categoryCode: SecurityCategory_CategoryCode.OPTIONS,
                            instrument: optionInstrument,
                            exchange,
                            underlyingSecurityCode: cs.code,
                            strikePrice: strike,
                            optionType: OptionType.CE,
                            hasFo: false,
                            derived: false,
                            exchangeSymbol: cs.exchangeSymbol,
                            name: optionSecurity.name
                        };
                        this.securities.set(getSecurityKey(exchange, ceCode), sec);
                        this.securityList.push(sec);
                        const peCode = `${codePrefix}${strike}_PE`;
                        sec = {
                            ...cs,
                            priceMultiplier: optionSecurity.priceMultiplier,
                            lotSize: optionSecurity.lotSize,
                            volumeFreezeQuantity: optionSecurity.volumeFreezeQuantity,
                            tickSize: optionSecurity.tickSize,
                            expiry: optionSecurity.expiry,
                            segment: Segment.FO,
                            series: 'XX',
                            symbol: `${symbolPrefix} ${(strike / (optionSecurity.priceMultiplier ?? 100)).toFixed(0)} PE`,
                            instrument: optionInstrument,
                            displayName: `${displayNamePrefix} ${(strike / (optionSecurity.priceMultiplier ?? 100)).toFixed(0)} PE`,
                            code: peCode,
                            underlyingSecurityCode: cs.code,
                            exchange,
                            strikePrice: strike,
                            optionType: OptionType.PE,
                            categoryCode: SecurityCategory_CategoryCode.OPTIONS,
                            hasFo: false,
                            derived: false,
                            exchangeSymbol: cs.exchangeSymbol,
                            name: optionSecurity.name
                        }
                        this.securities.set(getSecurityKey(exchange, peCode), sec);
                        this.securityList.push(sec);
                    })
                })
            })
        });
    }

    setSecurity(security: Security): void {
        const key = getSecurityKey(security.exchange, security.code);
        if (!this.securities.has(key)) {
            this.securities.set(getSecurityKey(security.exchange, security.code), security);
            this.securityList.push(security);
        }
    }

    getSecurities(keys: string[]): Security[] {
        return keys.map(key => this.getSecurity(key)).filter(sec => sec !== undefined);
    }
    getAllSecurities() {
        return this.securityList;
    }

    addSecurities(securities: Security[]) {
        securities.forEach(sec => {
            this.setSecurity(sec);
        })
    }

    getSecurity(key: string): Security | undefined {
        if (!key) return;
        return this.securities.get(key);
    }
}


export default SecurityManager;